"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Server = void 0;
const OperationBuffer_1 = require("./OperationBuffer");
const TextOperation_1 = require("./TextOperation");
// We only allow 100 operations, which should be more than
// enough to deal with latency. This number also represents
// how many operations can be applied between saves to
// do a "non destructive" diff when file writes
// are not matched by a revision
const MAX_OPERATIONS = 100;
class Server {
    document;
    clientRevisions = new Map();
    operationBuffer;
    /**
     * Create a server instance, provided a document as a string and an array of
     * operations.
     */
    constructor(document) {
        this.document = document;
        this.operationBuffer = new OperationBuffer_1.OperationBuffer();
    }
    /** Get the current server revision of the document */
    get revision() {
        return this.operationBuffer.revision;
    }
    /** Call this whenever a client disconnects from the server, this cleans up their data */
    addClient(clientId, revision) {
        this.clientRevisions.set(clientId, revision);
    }
    /** Call this whenever a client disconnects from the server, this cleans up their data */
    removeClient(clientId) {
        this.clientRevisions.delete(clientId);
    }
    /** Use this to get the client's ot state */
    getClientRevision(clientId) {
        return this.clientRevisions.get(clientId) ?? null;
    }
    /** Call this whenever a client acknowledges a server operation */
    ack(clientId, revision) {
        const clientRevision = this.clientRevisions.get(clientId);
        if (clientRevision != null && revision > clientRevision) {
            this.clientRevisions.set(clientId, revision);
        }
    }
    /**
     * This will clear as many operations as possible. Meaning that clients
     * on older revisions will still stay in history so we can still transform
     * their incoming operations
     */
    clearOperations() {
        // The result of any clear is a maximum of 100 operations
        const fromRevision = Math.max(this.revision - MAX_OPERATIONS, // 220 - 100
        Math.min(...this.clientRevisions.values()));
        this.operationBuffer.clear(fromRevision);
    }
    getOperations() {
        return this.operationBuffer.operations;
    }
    /** Call this method whenever you receive an operation from a client. */
    receiveOperation(revision, operation) {
        if (revision < 0 || this.operationBuffer.revision < revision) {
            throw new Error("operation revision not in history");
        }
        // Find all operations that the client didn't know of when it sent the operation
        const concurrentOperations = this.operationBuffer.slice(revision);
        // and transform the operation against all these newer operations
        for (let i = 0; i < concurrentOperations.length; i++) {
            operation = TextOperation_1.TextOperation.transform(operation, concurrentOperations[i])[0];
        }
        // we apply the transformed operation
        this.applyOperation(operation);
        // Create a new operation based on the transformed text operation and selections
        // This should be sent to all the other clients to ensure their documents are up to date
        return operation;
    }
    /** Call this method whenever you want to apply a server-generated operation. */
    applyOperation(operation) {
        // apply the operation on the document
        this.document = operation.apply(this.document);
        // Store operation in history
        this.operationBuffer.push(operation);
        // Let's make sure we are not exceeding 100 operations
        this.clearOperations();
    }
}
exports.Server = Server;
//# sourceMappingURL=data:application/json;base64,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